/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {AfterViewInit, Attribute, Directive, HostBinding, Optional} from '@angular/core';

import {AuthenticationService} from './authentication.service';

/**
 * Directive that help display or not a component according to the user authentication status.
 *
 * Usage:
 *
 * ```
 * <div gp-authenticated>Visible only if you're logged in</div>
 * <div gp-authenticated="false">Visible only if you're logged out</div>
 *
 * ```
 */
@Directive({
  selector: '[gp-authenticated]'
})
export class AuthenticatedDirective implements AfterViewInit {

  @HostBinding('class.hide') get display() {
    return !this.visible;
  }

  // Internal flag for whether or not the underlying content should be present in the DOM
  public visible: boolean = false;

  // Optional attribute passed to our directive that specifies if content should be displayed when user is
  // authenticated or not
  // - ```true``` (default) -> content should be displayed when user is authenticated, not displayed otherwise
  // - ```false``` -> the opposite
  public authenticatedAttribute: boolean = true;

  public ngAfterViewInit() {
    setTimeout(() => {
      // Wrap initialization call in a setTimeout, so that in "mock mode" we are sure the mocks are initialized
      // before triggering any HTTP call in AuthenticationService#isAuthenticated
      this.checkVisible();
      this.authenticationService.authenticationEvents.subscribe(this.checkVisible.bind(this));
    });
  }

  public checkVisible() {
    this.authenticationService.isAuthenticated().then(authenticated => {
      if (authenticated) {
        this.visible = this.authenticatedAttribute;
      } else {
        this.visible = !this.authenticatedAttribute;
      }
    });
  }

  constructor(private authenticationService: AuthenticationService,
              @Optional() @Attribute('gp-authenticated') authenticatedAttribute?: string) {
    this.authenticatedAttribute = authenticatedAttribute ?
      JSON.parse(authenticatedAttribute) :
      this.authenticatedAttribute;
  }
}
